import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { jwtDecode, JwtPayload } from "jwt-decode";

// Type Imports
import { AuthState, User } from "./authTypes";
import { IVenueApiKey } from "@pulse-ui/codegen/generated_ts/authn";

const initialState: AuthState = {
  token: null,
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  venueApiKeys: null,
};

const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<JwtPayload>(token);
    if (!decoded.exp) return true; // No expiration time means token is invalid/expired
    return decoded.exp * 1000 < Date.now();
  } catch (error) {
    return true; // Malformed token should be considered expired
  }
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setToken: (state, action: PayloadAction<string>) => {
      const token = action.payload;

      // If token is expired, clear state
      if (isTokenExpired(token)) {
        state.token = null;
        state.user = null;
        state.isAuthenticated = false;
        return;
      }

      state.token = token;
      state.isAuthenticated = true;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    clearAuth: (state) => {
      state.token = null;
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
    },
    setAuthError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearAuthError: (state) => {
      state.error = null;
    },
    setAuthLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setVenueApiKeysByAccountId: (
      state,
      action: PayloadAction<{ accountId: string; keys: IVenueApiKey[] }>
    ) => {
      state.venueApiKeys?.set(action.payload.accountId, action.payload.keys);
    },
    clearVenueApiKeysByAccountId: (state, action: PayloadAction<string>) => {
      state.venueApiKeys?.delete(action.payload);
    },
    setVenueApiKeys: (
      state,
      action: PayloadAction<Map<string, IVenueApiKey[]>>
    ) => {
      state.venueApiKeys = action.payload;
    },
    clearVenueApiKeys: (state) => {
      state.venueApiKeys = null;
    },
  },
});

export const {
  setToken,
  setUser,
  clearAuth,
  setAuthError,
  clearAuthError,
  setAuthLoading,
  setVenueApiKeys,
  clearVenueApiKeys,
} = authSlice.actions;

export default authSlice.reducer;
